import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Toast } from 'antd-mobile';
import API_ROUTES from '../api';

const memberFailInfoCache = new Map();

const convertTypeToImg = (type, index) => {
    let filter = '';
    let glow = '';

    switch (type) {
        case 'win':
            // 红色火焰 — 胜利 (低饱和度)
            filter = 'hue-rotate(0deg) saturate(1.1) brightness(0.9)';
            glow = '0 0 3px rgba(255, 60, 40, 0.3)';
            break;
        case 'draw':
            // 灰白色火焰 — 平局 (静谧)
            filter = 'grayscale(1) brightness(1.0) opacity(0.6)';
            glow = 'none';
            break;
        case 'fail':
            // 蓝色火焰 — 失败 (清冷)
            filter = 'hue-rotate(200deg) saturate(1.2) brightness(1.0)';
            glow = '0 0 3px rgba(30, 100, 255, 0.3)';
            break;
        default:
            return null;
    }

    return (
        <span
            key={index}
            title={type === 'win' ? '胜' : type === 'draw' ? '平' : '败'}
            style={{
                fontSize: '13px',
                lineHeight: 1,
                display: 'inline-block',
                filter: filter,
                textShadow: glow,
                margin: '0 -1px', // 紧凑排列
                flexShrink: 0,
                width: '10px',
                transform: 'scaleX(0.75)', // 水平压缩
                transformOrigin: 'center center'
            }}
        >
            🔥
        </span>
    );
};

// 核心数据处理
// trendType: 'winless' | 'unbeaten'
const processData = (data, trendType, count) => {
    if (!data) return [];

    const resultList = [];

    Object.entries(data).forEach(([memberId, records]) => {
        if (!records || records.length === 0) return;

        // 1. 按时间升序
        const sorted = [...records].sort(
            (a, b) => new Date(a.time) - new Date(b.time)
        );

        // 2. 根据组件参数决定走势类型
        const isSameTrend =
            trendType === 'unbeaten'
                ? (t) => t === 'win' || t === 'draw'   // 不败
                : (t) => t === 'fail' || t === 'draw'; // 不胜

        // 3. 从最近一场开始，反向收集连续走势
        const trend = [];
        for (let i = sorted.length - 1; i >= 0; i--) {
            const type = sorted[i].type;
            if (isSameTrend(type)) {
                trend.unshift(type); // 旧 → 新
            } else {
                break;
            }
        }

        if (trend.length >= count) {
            resultList.push({
                memberId,
                trend,
                count: trend.length
            });
        }
    });

    // 4. 按连续场数倒序
    resultList.sort((a, b) => b.count - a.count);
    return resultList;
};

const fetchMemberFailInfo = (memberIds) => {
    const cacheKey = memberIds;
    const cached = memberFailInfoCache.get(cacheKey);

    if (cached) {
        return cached instanceof Promise ? cached : Promise.resolve(cached);
    }

    // 首页会同时展示“不败”和“不胜”，同一批成员的接口结果可以复用，避免重复请求。
    const request = axios
        .get(API_ROUTES.game_member_fail_info, {
            params: { memberId: memberIds }
        })
        .then((res) => {
            const data = res.data.data || {};
            memberFailInfoCache.set(cacheKey, data);
            return data;
        })
        .catch((error) => {
            memberFailInfoCache.delete(cacheKey);
            throw error;
        });

    memberFailInfoCache.set(cacheKey, request);
    return request;
};

const MatchResultDisplay = ({
    memberBirthDay,
    count,
    trendType = 'winless' // 'winless' | 'unbeaten'
}) => {
    const [displayList, setDisplayList] = useState([]);

    const getFireDetailInfo = useCallback(() => {
        const showMembers = memberBirthDay
            ?.map((item) => item.id)
            ?.filter(Boolean)
            ?.sort()
            ?.join(',');

        if (!showMembers) {
            setDisplayList([]);
            return;
        }

        Toast.show({ icon: 'loading', content: '加载中...' });

        fetchMemberFailInfo(showMembers)
            .then((res) => {
                setDisplayList(processData(res, trendType, count));
            })
            .catch(() => {
                Toast.show({
                    icon: 'fail',
                    content: '查询失败，请联系管理员'
                });
                setDisplayList([]);
            })
            .finally(() => Toast.clear());
    }, [count, memberBirthDay, trendType]);

    useEffect(() => {
        if (memberBirthDay) {
            getFireDetailInfo();
        }
    }, [getFireDetailInfo, memberBirthDay]);

    const getMemberName = (memberId) => {
        const member = memberBirthDay?.find((item) => String(item.id) === String(memberId));
        if (!member) return '未知';
        const name = member.name || '';
        if (name.length === 2) {
            return name.charAt(0) + '　' + name.charAt(1);
        }
        return name;
    };

    return (
        <>
            {displayList.map((item) => {
                return (
                    <div className={`achievement-badge badge--${trendType}`} key={item.memberId}>
                        <div className="badge-text" style={{ color: '#4E5969' }}>
                            <span>{getMemberName(item.memberId)}</span>
                            <div className="home-fire-member-bars" style={{ display: 'flex', alignItems: 'center', gap: '1px', marginLeft: '4px' }}>
                                {item.trend.map((type, index) => convertTypeToImg(type, index))}
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default MatchResultDisplay;
